import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Award,
  CheckCircle2,
  ChevronDown,
  Lightbulb,
  Sparkles,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StrengthsAndAreas {
  strengths: string[];
  areasForImprovement: string[];
}

interface ProductRecommendation {
  productName: string;
  productType: string;
  reasoning: string;
  fitScore?: number;
}

interface InsuranceFeedbackData {
  strengths: string[];
  improvements: string[];
  productSuggestions: ProductRecommendation[];
  discoveryQuality: {
    score: number; // 0-100
    assessment: string;
    examples: string[];
    improvements: string[];
  };
  rapportBuilding: {
    score: number;
    assessment: string;
    examples: string[];
  };
  needsAnalysis: {
    score: number;
    assessment: string;
    keyQuestionsAsked: string[];
    missedOpportunities: string[];
  };
  closingStrategy: {
    score: number;
    assessment: string;
    nextSteps: string[];
  };
  overallScore: number;
  actionableNextSteps: Array<{
    action: string;
    description: string;
  }>;
}

interface InsuranceFeedbackAnalysisProps {
  feedbackData: InsuranceFeedbackData;
  language: 'en' | 'ko' | 'ja';
  onClose: () => void;
  clientName: string;
  duration: number; // in seconds
}

export function InsuranceFeedbackAnalysis({
  feedbackData,
  language,
  onClose,
  clientName,
  duration,
}: InsuranceFeedbackAnalysisProps) {
  const { t } = useTranslation();
  const isKorean = language === 'ko';
  const isJapanese = language === 'ja';

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['strengths', 'discovery', 'products']),
  );

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreRingColor = (score: number) => {
    if (score >= 80) return 'stroke-green-500';
    if (score >= 60) return 'stroke-yellow-500';
    return 'stroke-red-500';
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', duration: 0.5 }}
        className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col border border-white/20"
      >
        {/* Header */}
        <div className="px-4 py-4 md:px-8 md:py-6 border-b border-gray-200/50 flex items-start justify-between bg-white/50">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
              <h2 className="text-lg md:text-2xl font-bold text-gray-900 tracking-tight">
                {isKorean ? '세션 분석 리포트' : isJapanese ? 'セッション分析レポート' : 'Session Analysis Report'}
              </h2>
              <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-blue-50 text-blue-700 text-[10px] md:text-xs font-bold uppercase tracking-wider border border-blue-100">
                {isKorean ? 'AI 피드백' : isJapanese ? 'AIフィードバック' : 'AI Feedback'}
              </span>
            </div>
            <p className="text-gray-500 text-xs md:text-sm flex items-center gap-1.5 md:gap-2 flex-wrap">
              <span className="font-medium text-gray-700">{clientName}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300 hidden sm:block" />
              <span>{formatDuration(duration)}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300 hidden sm:block" />
              <span className="hidden sm:inline">{new Date().toLocaleDateString()}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <button
              onClick={onClose}
              className="p-1.5 md:p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-4 md:space-y-8 bg-gray-50/30">
          {/* Overall Score Card */}
          <div className="bg-white rounded-2xl p-4 md:p-8 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full blur-3xl -z-10 opacity-50" />

            <div className="flex-1 md:max-w-lg">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                {isKorean ? '전체 성과 요약' : isJapanese ? '全体パフォーマンス概要' : 'Overall Performance Summary'}
              </h3>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                {isKorean
                  ? '이번 세션에서 보여주신 상담 능력에 대한 종합적인 평가입니다. 전반적으로 우수한 소통 능력을 보여주셨으며, 특히 고객의 니즈를 파악하는 과정이 인상적이었습니다.'
                  : isJapanese
                    ? '今回のセッションでの相談スキルに対する総合的な評価です。全体的に優れたコミュニケーション能力を発揮され、特にニーズ分析が印象的でした。'
                    : 'A comprehensive assessment of your consultation skills in this session. You demonstrated excellent communication skills overall, with particularly impressive needs analysis.'}
              </p>
            </div>

            <div className="flex items-center justify-center gap-6 md:gap-8">
              <div className="text-center md:text-right">
                <div className="text-xs md:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  {isKorean ? '총점' : isJapanese ? '総合スコア' : 'Total Score'}
                </div>
                <div
                  className={`text-4xl md:text-5xl font-bold tracking-tight ${getScoreColor(
                    feedbackData.overallScore,
                  )}`}
                >
                  {feedbackData.overallScore}
                  <span className="text-xl md:text-2xl text-gray-400 ml-1 font-normal">
                    /100
                  </span>
                </div>
              </div>

              {/* Circular Progress */}
              <div className="relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="40"
                    cy="40"
                    r="32"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    className="text-gray-100 md:hidden"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    className="text-gray-100 hidden md:block"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="32"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={200.96}
                    strokeDashoffset={
                      200.96 - (200.96 * feedbackData.overallScore) / 100
                    }
                    className={`${getScoreRingColor(
                      feedbackData.overallScore,
                    )} transition-all duration-1000 ease-out md:hidden`}
                    strokeLinecap="round"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={251.2}
                    strokeDashoffset={
                      251.2 - (251.2 * feedbackData.overallScore) / 100
                    }
                    className={`${getScoreRingColor(
                      feedbackData.overallScore,
                    )} transition-all duration-1000 ease-out hidden md:block`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Award
                    className={`w-6 h-6 md:w-8 md:h-8 ${getScoreColor(
                      feedbackData.overallScore,
                    )}`}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Detailed Analysis */}
            <div className="lg:col-span-2 space-y-6">
              {/* Strengths & Areas for Improvement */}
              <FeedbackSection
                title={
                  isKorean
                    ? '강점 및 개선 사항'
                    : isJapanese
                      ? '強みと改善点'
                      : 'Strengths & Areas for Improvement'
                }
                icon={<Sparkles className="w-5 h-5" />}
                isExpanded={expandedSections.has('strengths')}
                onToggle={() => toggleSection('strengths')}
                language={language}
              >
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Strengths */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-gray-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </div>
                      {isKorean ? '강점' : isJapanese ? '強み' : 'Strengths'}
                    </h4>
                    <div className="space-y-2">
                      {feedbackData.strengths.map((strength, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="bg-green-50/50 border border-green-100 rounded-xl p-3 text-sm text-gray-700 leading-relaxed"
                        >
                          {strength}
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Areas for Improvement */}
                  <div className="space-y-3">
                    <h4 className="font-bold text-gray-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                      <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                        <TrendingUp className="w-3.5 h-3.5" />
                      </div>
                      {isKorean ? '개선 영역' : isJapanese ? '改善点' : 'Areas for Improvement'}
                    </h4>
                    <div className="space-y-2">
                      {feedbackData.improvements.map((improvement, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="bg-orange-50/50 border border-orange-100 rounded-xl p-3 text-sm text-gray-700 leading-relaxed"
                        >
                          {improvement}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </FeedbackSection>

              {/* Discovery Quality */}
              <FeedbackSection
                title={isKorean ? '발견 품질' : isJapanese ? 'ヒアリング品質' : 'Discovery Quality'}
                icon={<Target className="w-5 h-5" />}
                isExpanded={expandedSections.has('discovery')}
                onToggle={() => toggleSection('discovery')}
                score={feedbackData.discoveryQuality.score}
                language={language}
              >
                <div className="space-y-5">
                  <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 leading-relaxed">
                    {feedbackData.discoveryQuality.assessment}
                  </div>

                  {/* Examples */}
                  {feedbackData.discoveryQuality.examples.length > 0 && (
                    <div>
                      <h5 className="font-bold text-gray-900 mb-3 text-sm flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-yellow-500" />
                        {isKorean ? '우수 사례' : isJapanese ? '良い例' : 'Good Examples'}
                      </h5>
                      <div className="space-y-2">
                        {feedbackData.discoveryQuality.examples.map(
                          (example, idx) => (
                            <div
                              key={idx}
                              className="pl-4 border-l-2 border-green-400 py-1 text-sm text-gray-600 italic"
                            >
                              "{example}"
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </FeedbackSection>

              {/* Needs Analysis */}
              <FeedbackSection
                title={isKorean ? '니즈 분석' : isJapanese ? 'ニーズ分析' : 'Needs Analysis'}
                icon={<AlertCircle className="w-5 h-5" />}
                isExpanded={expandedSections.has('needs')}
                onToggle={() => toggleSection('needs')}
                score={feedbackData.needsAnalysis.score}
                language={language}
              >
                <div className="space-y-5">
                  <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 leading-relaxed">
                    {feedbackData.needsAnalysis.assessment}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* Key Questions Asked */}
                    {feedbackData.needsAnalysis.keyQuestionsAsked.length >
                      0 && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4">
                        <h5 className="font-bold text-gray-900 mb-3 text-xs uppercase tracking-wide text-green-700">
                          {isKorean
                            ? '질문한 핵심 사항'
                            : isJapanese
                              ? '質問した重要事項'
                              : 'Key Questions Asked'}
                        </h5>
                        <ul className="space-y-2">
                          {feedbackData.needsAnalysis.keyQuestionsAsked.map(
                            (question, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-2 text-sm text-gray-600"
                              >
                                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{question}</span>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Missed Opportunities */}
                    {feedbackData.needsAnalysis.missedOpportunities.length >
                      0 && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4">
                        <h5 className="font-bold text-gray-900 mb-3 text-xs uppercase tracking-wide text-red-700">
                          {isKorean ? '놓친 기회' : isJapanese ? '見逃した機会' : 'Missed Opportunities'}
                        </h5>
                        <ul className="space-y-2">
                          {feedbackData.needsAnalysis.missedOpportunities.map(
                            (opportunity, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-2 text-sm text-gray-600"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" />
                                <span>{opportunity}</span>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </FeedbackSection>
            </div>

            {/* Right Column - Actionable Steps & Products */}
            <div className="space-y-6">
              {/* Actionable Next Steps */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-4 py-3 md:px-6 md:py-4">
                  <h3 className="font-bold text-white flex items-center gap-2 text-sm md:text-base">
                    <Target className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
                    {isKorean
                      ? '실행 가능한 다음 단계'
                      : isJapanese
                        ? '実行可能な次のステップ'
                        : 'Actionable Next Steps'}
                  </h3>
                </div>
                <div className="p-4 md:p-6 space-y-3 md:space-y-4">
                  {feedbackData.actionableNextSteps.map((step, idx) => (
                    <div key={idx} className="flex gap-3 md:gap-4">
                      <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-blue-50 text-blue-600 font-bold flex items-center justify-center text-xs md:text-sm border border-blue-100">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-gray-900 text-xs md:text-sm mb-1">
                          {step.action}
                        </h4>
                        <p className="text-[10px] md:text-xs text-gray-500 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Product Suggestions */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm md:text-base">
                    <Lightbulb className="w-4 h-4 md:w-5 md:h-5 text-yellow-500" />
                    {isKorean ? 'AI 상품 제안' : isJapanese ? 'AI商品提案' : 'AI Product Suggestions'}
                  </h3>
                </div>
                <div className="p-3 md:p-4 space-y-2 md:space-y-3">
                  {feedbackData.productSuggestions.map((product, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-50 rounded-xl p-3 md:p-4 border border-gray-100 hover:border-blue-200 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 text-xs md:text-sm">
                            {product.productName}
                          </h4>
                          <p className="text-[10px] md:text-xs text-gray-500">
                            {product.productType}
                          </p>
                        </div>
                        {product.fitScore && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] md:text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                            {product.fitScore}%
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] md:text-xs text-gray-600 leading-relaxed border-t border-gray-200 pt-2 mt-2">
                        {product.reasoning}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white px-4 py-4 md:px-8 md:py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[10px] md:text-xs text-gray-400 text-center sm:text-left">
            {isKorean
              ? 'AI 분석 결과는 참고용이며, 실제 상담 상황에 따라 다를 수 있습니다.'
              : isJapanese
                ? 'AI分析結果は参考用であり、実際の相談状況により異なる場合があります。'
                : 'AI analysis results are for reference only and may vary based on actual consultation context.'}
          </p>
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 md:px-8 py-2 md:py-2.5 bg-gray-900 text-white rounded-lg md:rounded-xl hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl font-semibold text-sm"
          >
            {isKorean ? '닫기' : isJapanese ? '閉じる' : 'Close'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Collapsible Section Component
function FeedbackSection({
  title,
  icon,
  isExpanded,
  onToggle,
  children,
  score,
  language,
}: {
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  score?: number;
  language: 'en' | 'ko' | 'ja';
}) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-100';
    return 'text-red-600 bg-red-50 border-red-100';
  };
  const isKorean = language === 'ko';
  const isJapanese = language === 'ja';

  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 md:p-5 bg-white hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
          <div
            className={`w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 ${
              isExpanded
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                : 'bg-gray-100 text-gray-500'
            } transition-all duration-300`}
          >
            <div className="w-4 h-4 md:w-5 md:h-5">{icon}</div>
          </div>
          <div className="text-left flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm md:text-lg truncate">{title}</h3>
            {!isExpanded && score !== undefined && (
              <div className="text-[10px] md:text-xs text-gray-500 font-medium mt-0.5">
                {isKorean ? '점수' : isJapanese ? 'スコア' : 'Score'}: {score}/100
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-4 ml-2">
          {score !== undefined && (
            <div
              className={`px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-bold border ${getScoreColor(
                score,
              )}`}
            >
              {score}
            </div>
          )}
          <div
            className={`p-1.5 md:p-2 rounded-full transition-transform duration-300 ${
              isExpanded ? 'rotate-180 bg-gray-100' : ''
            }`}
          >
            <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
          </div>
        </div>
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="border-t border-gray-100"
          >
            <div className="p-4 md:p-6 bg-white">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
